'use client';

import { c as _c } from "react/compiler-runtime";
import { useState } from 'react';
import { Image as ImageIcon, Link2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import BetterUploader from '@/components/BetterUploader';
import { FilePickerDialog } from '@/components/files';
interface EditorImageDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onInsert: (url: string, alt?: string) => void;
}

/**
 * A dialog for inserting images into the editor.
 * Supports:
 * - Entering a URL directly
 * - Uploading a new file
 * - Browsing the file library
 */
export function EditorImageDialog(t0) {
  const $ = _c(50);
  const {
    open,
    onOpenChange,
    onInsert
  } = t0;
  const [url, setUrl] = useState("");
  const [alt, setAlt] = useState("");
  const [showFilePicker, setShowFilePicker] = useState(false);
  let handleFileSelect;
  let handleInsertUrl;
  let handleUploadComplete;
  if ($[0] !== alt || $[1] !== onInsert || $[2] !== onOpenChange || $[3] !== url) {
    handleInsertUrl = () => {
      if (url.trim()) {
        onInsert(url.trim(), alt.trim() || undefined);
        reset();
      }
    };
    handleUploadComplete = data => {
      const uploadedUrl = data?.metadata?.files[0]?.url;
      if (uploadedUrl) {
        onInsert(uploadedUrl, alt.trim() || undefined);
        reset();
      }
    };
    handleFileSelect = files => {
      if (files && files.length > 0) {
        onInsert(files[0].url, alt.trim() || files[0].name || undefined);
        reset();
      }
      setShowFilePicker(false);
    };
    const reset = () => {
      setUrl("");
      setAlt("");
      onOpenChange(false);
    };
    $[0] = alt;
    $[1] = onInsert;
    $[2] = onOpenChange;
    $[3] = url;
    $[4] = handleFileSelect;
    $[5] = handleInsertUrl;
    $[6] = handleUploadComplete;
  } else {
    handleFileSelect = $[4];
    handleInsertUrl = $[5];
    handleUploadComplete = $[6];
  }
  let t1;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <DialogHeader><DialogTitle className="flex items-center gap-2"><ImageIcon className="w-4 h-4" />Insert Image</DialogTitle></DialogHeader>;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  let t2;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Label htmlFor="editor-img-alt">Alt Text (optional)</Label>;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = e => setAlt(e.target.value);
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== alt) {
    t4 = <div className="space-y-1.5">{t2}<Input id="editor-img-alt" value={alt} onChange={t3} placeholder="Image description" /></div>;
    $[10] = alt;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <TabsList className="grid w-full grid-cols-3"><TabsTrigger value="url" className="cursor-pointer">URL</TabsTrigger><TabsTrigger value="upload" className="cursor-pointer">Upload</TabsTrigger><TabsTrigger value="library" className="cursor-pointer">Library</TabsTrigger></TabsList>;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Label htmlFor="editor-img-url">Image URL</Label>;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = e_0 => setUrl(e_0.target.value);
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== handleInsertUrl) {
    t8 = e_1 => {
      if (e_1.key === "Enter") {
        e_1.preventDefault();
        handleInsertUrl();
      }
    };
    $[15] = handleInsertUrl;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t8 || $[18] !== url) {
    t9 = <div className="space-y-1.5">{t6}<Input id="editor-img-url" value={url} onChange={t7} placeholder="https://..." onKeyDown={t8} /></div>;
    $[17] = t8;
    $[18] = url;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== url) {
    t10 = url.trim();
    $[20] = url;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  const t11 = !t10;
  let t12;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <Link2 className="w-4 h-4 mr-1.5" />;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== handleInsertUrl || $[24] !== t11) {
    t13 = <DialogFooter><Button onClick={handleInsertUrl} disabled={t11} className="cursor-pointer">{t12}Insert</Button></DialogFooter>;
    $[23] = handleInsertUrl;
    $[24] = t11;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== t13 || $[27] !== t9) {
    t14 = <TabsContent value="url" className="space-y-3 pt-2">{t9}{t13}</TabsContent>;
    $[26] = t13;
    $[27] = t9;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== handleUploadComplete) {
    t15 = <TabsContent value="upload" className="pt-2"><BetterUploader mode="single" accept="image/*" onUploadComplete={handleUploadComplete} compress={true} /></TabsContent>;
    $[29] = handleUploadComplete;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = () => setShowFilePicker(true);
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  let t17;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <TabsContent value="library" className="pt-2"><Button variant="outline" onClick={t16} className="w-full cursor-pointer"><ImageIcon className="w-4 h-4 mr-1.5" />Browse File Library</Button></TabsContent>;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  let t18;
  if ($[33] !== t14 || $[34] !== t15) {
    t18 = <Tabs defaultValue="url" className="w-full">{t5}<div className="min-h-[120px]">{t14}{t15}{t17}</div></Tabs>;
    $[33] = t14;
    $[34] = t15;
    $[35] = t18;
  } else {
    t18 = $[35];
  }
  let t19;
  if ($[36] !== t18 || $[37] !== t4) {
    t19 = <DialogContent className="sm:max-w-md">{t1}<div className="space-y-3">{t4}{t18}</div></DialogContent>;
    $[36] = t18;
    $[37] = t4;
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  let t20;
  if ($[39] !== onOpenChange || $[40] !== open || $[41] !== t19) {
    t20 = <Dialog open={open} onOpenChange={onOpenChange}>{t19}</Dialog>;
    $[39] = onOpenChange;
    $[40] = open;
    $[41] = t19;
    $[42] = t20;
  } else {
    t20 = $[42];
  }
  let t21;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = ["media"];
    $[43] = t21;
  } else {
    t21 = $[43];
  }
  let t22;
  if ($[44] !== handleFileSelect || $[45] !== showFilePicker) {
    t22 = <FilePickerDialog open={showFilePicker} onOpenChange={setShowFilePicker} onSelect={handleFileSelect} selectionMode="single" title="Select Image" initialType={t21} initialStatus="active" />;
    $[44] = handleFileSelect;
    $[45] = showFilePicker;
    $[46] = t22;
  } else {
    t22 = $[46];
  }
  let t23;
  if ($[47] !== t20 || $[48] !== t22) {
    t23 = <>{t20}{t22}</>;
    $[47] = t20;
    $[48] = t22;
    $[49] = t23;
  } else {
    t23 = $[49];
  }
  return t23;
}